/**
 * The Settings routes a fold retired, each with the address that answers for it
 * now (decision 0584). The target is the group's own route plus the section's
 * anchor, and the anchor is the last segment of the retired route itself, so the
 * rule needs no lookup table beyond this one. The one exception is the sales tax
 * fold of decision 0707, whose three routes land on sections named for what they
 * hold; `NAMED_SECTION_ROUTES` lists them.
 *
 * 🔒 Every one of these still answers. A person who bookmarked a setting, a
 * message that links to one, and a standing check written against the page it
 * used to be all land on the section that holds it.
 *
 * 🔨 A retired route carries no Settings Index row. The group's row is what
 * reaches the setting; a second row would be two doors onto one section, and the
 * Index is a list of places rather than a list of settings.
 */
import { MAIL_SENDING_SETTINGS_ROUTE, MAIL_SETTINGS_ROUTE, mailSettingsSectionRoute } from '@bms2/shared';

/** 🔒 Decision 1648: the retired Vendor Keys address, which still answers by landing on Settings › Integrations. */
export const VENDOR_KEYS_RETIRED_ROUTE = '/settings/banking/vendor-keys';

/** 🔒 Decision 2281: the checklist templates' old Settings address, which lands on the Knowledge section's Checklists tab. */
export const CHECKLIST_TEMPLATES_RETIRED_ROUTE = '/settings/service/checklist-templates';

/** 🔒 Decision 0707: the one Settings page for sales tax, which the Tax category's entry opens. */
export const SALES_TAX_ROUTE = '/settings/tax/sales-tax';

/** 🔒 Decision 1393: the Organization profile, which Display formats and Email domains fold into as sections. */
export const ORGANIZATION_PROFILE_ROUTE = '/settings/company/organisation';

/**
 * The retired routes whose section is named for what it holds rather than for the route's last segment: the sales
 * tax fold (decision 0707), and the two Display formats addresses, which land on the profile's Regional section
 * beside the zone (decision 1393).
 */
export const NAMED_SECTION_ROUTES: readonly string[] = [
  '/accounting/tax', '/settings/tax/rates', '/settings/tax/forms', '/settings/accounting/sales-tax-rate-table',
  '/settings/company/display-formats', '/settings/accounting/quantity-precision',
];

export const RETIRED_SETTINGS_ROUTES: readonly (readonly [string, string])[] = [
  // The owner's own example: which basis reports open on, what stock is costed at, and the cash-basis timing.
  ['/settings/accounting/reporting-basis', '/settings/accounting/basis#reporting-basis'],
  ['/settings/accounting/cost-basis', '/settings/accounting/basis#cost-basis'],
  ['/settings/accounting/cash-basis-cogs', '/settings/accounting/basis#cash-basis-cogs'],
  // The two approval editors piece 15 already described as standing beside one another.
  ['/settings/accounting/approval-rules', '/settings/accounting/approvals#approval-rules'],
  ['/settings/accounting/approval-tiers', '/settings/accounting/approvals#approval-tiers'],
  // A state's rate and the warning raised against that state's threshold, on the one Sales tax page since decision 0707.
  ['/settings/accounting/sales-tax-rate-table', `${SALES_TAX_ROUTE}#register`],
  ['/settings/accounting/nexus-thresholds', `${SALES_TAX_ROUTE}#nexus-thresholds`],
  /*
   * 🔒 Decision 0707 (T228): the three Tax entries became one. The register left `/accounting/tax`, and the
   * rates and the forms left their own Settings pages. 🔨 Each lands on the section holding what it opened,
   * named for that section rather than for the route's last segment: `rates` would have been a second anchor
   * on the rate table, which the route above already reaches as `sales-tax-rate-table`.
   */
  ['/accounting/tax', `${SALES_TAX_ROUTE}#register`],
  // 🔒 Decision 0741 (T284): the rate table and the forms list are parts of each state's line in the register, so
  // both land there, and so does the older rate-table address above.
  ['/settings/tax/rates', `${SALES_TAX_ROUTE}#register`],
  ['/settings/tax/forms', `${SALES_TAX_ROUTE}#register`],
  // The requirement types, above the requirements set against them.
  ['/settings/company/site-requirement-types', '/settings/company/site-requirements#site-requirement-types'],
  // What a file is classified as, and what is refused outright.
  ['/settings/documents/blocked-extensions', '/settings/documents/file-types#blocked-extensions'],
  // What a customer may owe, and what being late costs them.
  ['/settings/accounting/credit-limit', '/settings/accounting/customer-credit#credit-limit'],
  ['/settings/accounting/finance-charge', '/settings/accounting/customer-credit#finance-charge'],
  // The one entry that crosses categories: a quantity's decimals is a display format, on the profile's Regional section (decision 1393).
  ['/settings/accounting/quantity-precision', `${ORGANIZATION_PROFILE_ROUTE}#regional`],
  // The rate a mileage claim pays, beside the categories an expense posts through.
  ['/settings/accounting/mileage-rate', '/settings/accounting/expense-categories#mileage-rate'],
  // The footer every outbound message ends on, beside the templates they are sent with.
  ['/settings/email/legal-footer', '/settings/accounting/email-templates#legal-footer'],
  // Who a website lead is refused from, beside where one is routed.
  ['/settings/crm/lead-blocklist', '/settings/crm/website-lead-routing#lead-blocklist'],
  /*
   * 🔒 Decision 1391 (T580): Mailboxes and Sending were Index entries and sections of the Mail Settings window at
   * once. The window is the one editor now, so both addresses land in it: the mailbox list on the window's own
   * Mailboxes page, whose address ends on the same segment, and Sending on that page's section of the same name.
   */
  [MAIL_SETTINGS_ROUTE, mailSettingsSectionRoute('mailboxes')],
  [MAIL_SENDING_SETTINGS_ROUTE, `${mailSettingsSectionRoute('mailboxes')}#sending`],  /*
   * 🔒 Decision 1393 (T581): Display formats and Email domains folded into Organization profile. The date format
   * lands beside the zone on Regional; the domains keep their own anchor, the route's last segment.
   */
  ['/settings/company/display-formats', `${ORGANIZATION_PROFILE_ROUTE}#regional`],
  ['/settings/company/email-domains', `${ORGANIZATION_PROFILE_ROUTE}#email-domains`],
  /*
   * 🔒 Decision 2790 (T1610): seventeen entries about one job became five rules pages and a section. The anchor is
   * the retired route's own last segment, and the group's route is written out as a literal in each line.
   */
  // Document Rules: what a won quote offers, the stale date, the RFQ chase and the credit note's non-returnable line.
  ['/settings/accounting/auto-conversion', '/settings/accounting/document-rules#auto-conversion'],
  ['/settings/accounting/stale-send-warning', '/settings/accounting/document-rules#stale-send-warning'],
  ['/settings/accounting/rfq-chase-defaults', '/settings/accounting/document-rules#rfq-chase-defaults'],
  ['/settings/accounting/non-returnable-line', '/settings/accounting/document-rules#non-returnable-line'],
  // Payment Rules, under Banking & Payments; Card Surcharge crosses here from Accounting.
  ['/settings/accounting/bill-variance-tolerance', '/settings/banking/payment-rules#bill-variance-tolerance'],
  ['/settings/accounting/match-confidence-tiers', '/settings/banking/payment-rules#match-confidence-tiers'],
  ['/settings/banking/check-numbering', '/settings/banking/payment-rules#check-numbering'],
  ['/settings/accounting/card-surcharge', '/settings/banking/payment-rules#card-surcharge'],
  // Dispatch Rules, under Service.
  ['/settings/accounting/emergency-approvals', '/settings/service/dispatch-rules#emergency-approvals'],
  ['/settings/service/schedule-notices', '/settings/service/dispatch-rules#schedule-notices'],
  ['/settings/accounting/travel-billing', '/settings/service/dispatch-rules#travel-billing'],
  // Pipeline Rules, under CRM.
  ['/settings/crm/quick-deal-rules', '/settings/crm/pipeline-rules#quick-deal-rules'],
  ['/settings/crm/follow-up-rules', '/settings/crm/pipeline-rules#follow-up-rules'],
  // HR Rules, under HR.
  ['/settings/hr/escalation-route', '/settings/hr/hr-rules#escalation-route'],
  ['/settings/hr/document-retention', '/settings/hr/hr-rules#document-retention'],
  ['/settings/hr/day-counts', '/settings/hr/hr-rules#day-counts'],
  // The four print margins a new template is seeded with, the last section of the Print Templates page.
  ['/settings/documents/print-defaults', '/settings/documents/print-templates#print-defaults'],
];
